import {
	Alert,
	AlertDescription,
	BottomSheet,
	Button,
} from "@ancatag/at-design";
import { AlertTriangle, Settings } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { NsfwConsentModal } from "../components/auth/NsfwConsentModal";
import {
	ComingSoon,
	PlaygroundChat,
	PlaygroundConfigPanel,
	type PlaygroundSelection,
	PlaygroundTopBar,
} from "../components/playground";
import { useAuth } from "../context/AuthContext";
import { useMobile } from "../hooks/useMobile";
import { giveNsfwConsent } from "../services/auth";
import type { ChatCompletionParams } from "../services/playground";
import { clearConversation } from "../services/playground";
import { getSandboxToken } from "../services/playground-sandbox";
import { listMyRouteConfigs } from "../services/route-configs";

// ═══════════════════════════════════════════════════════════════════════════
// PLAYGROUND PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function Playground() {
	const [, setLocation] = useLocation();
	const { isMobile } = useMobile();
	const { user, checkAuth } = useAuth();
	const [configPanelOpen, setConfigPanelOpen] = useState(false);
	const [consentModalOpen, setConsentModalOpen] = useState(false);
	// Selection state (route-config centric)
	const [selection, setSelection] = useState<PlaygroundSelection>({
		routeConfig: null,
		generationType: "text",
	});
	const [sandboxToken, setSandboxToken] = useState<string | null>(null);
	const [loadingToken, setLoadingToken] = useState(true);

	// Parameter overrides for testing
	const [params, setParams] = useState<ChatCompletionParams>({
		temperature: 0.7,
		max_tokens: 2048,
		top_p: 0.9,
	});
	const [systemPrompt, setSystemPrompt] = useState("");

	// Refresh key to force reload of model configs
	const [refreshKey] = useState(0);

	// Track if current model has messages
	const [hasMessages, setHasMessages] = useState(false);

	// Force chat component to reload when cleared
	const [chatRefreshKey, setChatRefreshKey] = useState(0);

	// Clear conversation for current route config
	const handleClearConversation = useCallback(() => {
		if (selection.routeConfig?.id) {
			clearConversation(selection.routeConfig.id);
			setHasMessages(false);
			setChatRefreshKey((prev) => prev + 1); // Force chat reload
			toast.success("Conversation cleared");
		}
	}, [selection.routeConfig?.id]);

	// Load sandbox token on mount and when route config changes
	useEffect(() => {
		if (!selection.routeConfig?.id) {
			return; // Don't fetch token if no route config selected
		}

		(async () => {
			setLoadingToken(true);
			try {
				console.log(
					"[Playground] Fetching sandbox token for route config:",
					selection.routeConfig?.id,
				);
				const tokenData = await getSandboxToken(selection.routeConfig?.id);
				setSandboxToken(tokenData.token);
				console.log("[Playground] Sandbox token fetched successfully");
			} catch (error: any) {
				console.error("[Playground] Failed to fetch sandbox token:", error);
				// Check if it's a consent error
				if (
					error.code === "FORBIDDEN" ||
					error.message?.includes("consent") ||
					error.message?.includes("NSFW")
				) {
					console.log(
						"[Playground] Consent required - this should be handled by consent modal",
					);
					// Don't show error toast - consent modal should handle this
				} else {
					toast.error("Sandbox unavailable", {
						description:
							"Could not fetch sandbox token. Please retry or re-login.",
					});
				}
			} finally {
				setLoadingToken(false);
			}
		})();
	}, [selection.routeConfig?.id]);

	const handleSelectionChange = useCallback(
		(newSelection: PlaygroundSelection) => {
			setSelection(newSelection);
		},
		[],
	);

	const handleCreateConfig = useCallback(() => {
		setLocation("/route-configs/new");
	}, [setLocation]);

	// Check for NSFW models on mount and show consent if needed
	useEffect(() => {
		const checkNsfwConsent = async () => {
			// Only check if user hasn't consented yet
			if (user?.nsfwConsentGiven) {
				return;
			}

			try {
				// Check if user has any NSFW route configs
				const configs = await listMyRouteConfigs({
					page: 1,
					limit: 100,
					isActive: true,
				});
				const hasNsfwConfigs = (configs.data || []).some(
					(config) => config.isNsfw,
				);

				if (hasNsfwConfigs) {
					console.log(
						"[Playground] User has NSFW route configs but no consent, showing consent modal",
					);
					setConsentModalOpen(true);
				}
			} catch (error) {
				console.error("[Playground] Error checking NSFW route configs:", error);
			}
		};

		if (user) {
			checkNsfwConsent();
		}
	}, [user]);

	const handleConsent = async () => {
		try {
			await giveNsfwConsent();
			await checkAuth(); // Refresh user data
			toast.success("Consent recorded. You can now access NSFW models.");
			setConsentModalOpen(false);
		} catch (error: any) {
			toast.error(error.message || "Failed to record consent");
			throw error;
		}
	};

	return (
		<div className="min-h-[calc(100vh-4rem)] flex flex-col px-4 py-4 md:px-6 md:py-6 bg-background gap-4">
			{/* NSFW Warning Banner */}
			{selection.routeConfig?.isNsfw && (
				<Alert className="border-orange-500/50 bg-orange-500/10">
					<AlertTriangle className="h-4 w-4 text-orange-500" />
					<AlertDescription className="text-sm">
						<span className="font-semibold">NSFW Route Config Active:</span> You
						are currently using a route config marked as NSFW (Not Safe For
						Work). This route config may generate mature content intended for
						adults 18 years or older. Please use responsibly.
						{selection.routeConfig.nsfwWarning && (
							<span className="block mt-1 text-muted-foreground">
								{selection.routeConfig.nsfwWarning}
							</span>
						)}
					</AlertDescription>
				</Alert>
			)}

			{/* Top Bar with selectors */}
			<div className="rounded-lg border border-border bg-card shadow-sm">
				<PlaygroundTopBar
					key={refreshKey}
					selection={selection}
					onSelectionChange={handleSelectionChange}
					onCreateConfig={handleCreateConfig}
					onClearConversation={handleClearConversation}
					hasMessages={hasMessages}
				/>
			</div>

			{/* Main Content Area */}
			<div className="flex-1 flex overflow-hidden rounded-lg border border-border bg-card shadow-sm min-h-0 relative">
				{/* Chat Panel */}
				<div className="flex-1 flex flex-col min-w-0">
					{selection.generationType === "text" ? (
						<PlaygroundChat
							authToken={sandboxToken}
							routeConfig={selection.routeConfig}
							systemPrompt={
								systemPrompt || selection.routeConfig?.systemPrompt || undefined
							}
							params={params}
							isReady={
								!!sandboxToken && !!selection.routeConfig && !loadingToken
							}
							onMessagesChange={setHasMessages}
							refreshKey={chatRefreshKey}
						/>
					) : (
						<ComingSoon type={selection.generationType} />
					)}
				</div>

				{/* Config Panel - Desktop: Sidebar, Mobile: Bottom Sheet */}
				{selection.generationType === "text" &&
					(!isMobile ? (
						<div className="w-80 flex-shrink-0 border-l border-border bg-card hidden md:block">
							<PlaygroundConfigPanel
								routeConfig={selection.routeConfig}
								params={params}
								systemPrompt={systemPrompt}
								onParamsChange={setParams}
								onSystemPromptChange={setSystemPrompt}
							/>
						</div>
					) : (
						<>
							{/* Floating Action Button for Mobile */}
							<Button
								size="icon"
								className="fixed bottom-20 right-4 h-12 w-12 rounded-full shadow-lg z-40 md:hidden"
								onClick={() => setConfigPanelOpen(true)}
								aria-label="Open config panel"
							>
								<Settings className="h-5 w-5" />
							</Button>

							{/* Bottom Sheet for Config Panel */}
							<BottomSheet
								open={configPanelOpen}
								onOpenChange={setConfigPanelOpen}
								title="Route Configuration"
								maxHeight="max-h-[85vh]"
							>
								<PlaygroundConfigPanel
									routeConfig={selection.routeConfig}
									params={params}
									systemPrompt={systemPrompt}
									onParamsChange={setParams}
									onSystemPromptChange={setSystemPrompt}
								/>
							</BottomSheet>
						</>
					))}
			</div>

			{/* NSFW Consent Modal */}
			<NsfwConsentModal
				open={consentModalOpen}
				onOpenChange={setConsentModalOpen}
				onConsent={handleConsent}
			/>
		</div>
	);
}
